import { RWSService } from "@rws-framework/client";
import StorageService, {
  type StorageServiceInstance,
} from "./StorageService";
import ContentsService, {
  type ContentsServiceInstance,
} from "./ContentsService";
import JunctionUserService, {
  type JunctionUserServiceInstance,
} from "./JunctionUserService";
import CourseDashboardService, {
  type CourseDashboardServiceInstance,
} from "./CourseDashboardService";
import { HTTPClient } from "./httpClient";
import type { Activity } from "../types/activities";
import { ActivityModel } from "../types/activities";
import type { Course } from "../types/course";
import type { User } from "../components/programs/typesRaw";
import { getAYearFromNow } from "src/helpers/dateUtils";

interface SetIlistOptions {
  vmglobal_time: string;
  vmtime_zone: string;
  vmchanges: Record<string, Partial<Activity>>;
}

export interface ResumeData {
  blocked?: boolean;
  default: {
    submission_attempt_override?: boolean;
    max_attempts?: number;
    [key: string]: unknown;
  };
}

interface DefaultResumeAttempts {
  submission_attempt_override?: boolean;
  max_attempts?: number;
  reverse_policy: boolean;
  [key: string]: unknown;
}

type AccessDenied = { denied: 'assistant_no_mgr_rights' | 'not_instructor' };
type Warning = { message: string; duration: number };
type InitResult = {
  accessDenied?: AccessDenied;
  warning?: Warning;
  data?: {
    course: Course;
    globalTime: string;
    notBlocked: boolean;
    defaultResumeAttempts: DefaultResumeAttempts;
    allActivities: Activity[];
  };
};


const SECTION_TEMPLATE_WARNING = {
  message:
    "⚠️ Warning! Saving changes here will break the linkage to the section template. Future changes to the section template will not be reflected in your section.",
  duration: 12000,
};

class ActivitySettingsV2Service extends RWSService {
  static _IN_CLIENT: boolean = true;

  private client = new HTTPClient({});

  constructor(
    @StorageService private storageService: StorageServiceInstance,
    @ContentsService private contentsService: ContentsServiceInstance,
    @JunctionUserService private junctionUserService: JunctionUserServiceInstance,
    @CourseDashboardService private dashboardService: CourseDashboardServiceInstance,
  ) {
    super();
  }

  public async init(
    courseId: string,
    timeZone: string,
  ): Promise<InitResult> {
    const [course, activities, resume, user] =
      await Promise.all([
        this.getCourse(courseId),
        this.dashboardService.get("activities"),
        this.getResumeData(courseId),
        this.junctionUserService.getUser(),
      ]);

    const accessDenied = this.checkPermissions(course, user);
    if (accessDenied) return { accessDenied };

    const warning = getSectionTemplateWarning(course);
    const globalTime = this.time_convert(course.global_time, timeZone);
    const { notBlocked, defaultResumeAttempts } = processResumeData(resume);

    const allActivities = this.set_ilist(activities, {
      vmglobal_time: globalTime,
      vmtime_zone: timeZone,
      vmchanges: {},
    });

    return {
      warning,
      data: {
        course,
        globalTime,
        notBlocked,
        defaultResumeAttempts,
        allActivities,
      },
    };
  }

  public async refetchActivities(
    courseGlobalTime: string,
    timeZone: string,
  ): Promise<{ allActivities: Activity[] }> {
    this.dashboardService.flush("activities");
    const activities = await this.dashboardService.get("activities");

    const globalTime = this.time_convert(courseGlobalTime, timeZone);
    const allActivities = this.set_ilist(activities, {
      vmglobal_time: globalTime,
      vmtime_zone: timeZone,
      vmchanges: {},
    });

    return { allActivities };
  }

  public clearStorage(): void {
    this.storageService.clearSome();
  }

  private getCourse(courseId: string): Promise<Course> {
    return new Promise((resolve) => {
      this.storageService.get("course", { courseId }, resolve);
    });
  }

  public async getResumeData(courseId: string): Promise<ResumeData> {
    // assessment:resume:attempts
    return this.client.get("/assessment/resume/" + courseId);
  }

  public set_ilist(
    vmunfiltered: Activity[],
    { vmglobal_time, vmtime_zone, vmchanges }: SetIlistOptions,
  ): Activity[] {
      const nextyear = getAYearFromNow().toISOString();
      function set_open_date_values(a) {
        [a.oopen_date, a.open_time] = formatDate(a.open_date, vmglobal_time, vmtime_zone);
        if (a.open_date && a.suppress_until_available) {
          a.oopen_date = (a.open_date < nextyear? a.oopen_date : "-");
          a.locked = true;
        } else {
          a.locked = false;
        }
      }

      function set_close_date_values(a) {
        [a.cclose_date, a.close_time] = formatDate(a.close_date, vmglobal_time, vmtime_zone);
      }

      function set_due_date_values(a) {
        [a.ddue_date, a.due_time] = formatDate(a.due_date, vmglobal_time, vmtime_zone);

        if ((vmchanges[a.metaid]||{}).hasOwnProperty('due_date')) {
          // this seems to be the case where the view is changed while still
          // waiting for an update call to return from the server...
          [a.ddue_date, a.due_time] = formatDate(vmchanges[a.metaid].due_date, vmglobal_time, vmtime_zone);
        }
      }


      return vmunfiltered.map((a, index) => {
      a.metaid = a.type == "custom" ? `_${a.id}` : a.id;
      a.dlink = ActivityModel.getDisplayLink(a, vmunfiltered, index);
      a.dtitle = a.report_title;
      a.sscoring_type = a.course_data_only ? "-" : (a.scoring_type ? a.scoring_type.replace('--', '-') : "Manual");
      a.ttype = ActivityModel.getTypeLabel(a);
      a.ttime = ActivityModel.getTimeLimit(a);
      a.resp_avail = ActivityModel.getResponsesAvailable(a);
      a.ssubmission_attempts = a.type === "assessment"? (a.submission_attempts || '∞') : '';

      set_open_date_values(a);
      set_close_date_values(a);
      set_due_date_values(a);

      // RESUME REDUX the activities have these applied (with override policy)
      a.resume_attempts = a.resume_max_attempts;

      return a;
    });

  }

  public time_convert = (
      hours: string | null,
      tz?: string,
    ): string => {
      if (tz && !tz.includes("local")) {
        const [h, m] = (hours || "23:59").split(":").map((n) => parseInt(n));
        return window.luxon.DateTime.utc(1970, 1, 1, h, m)
          .setZone(tz)
          .toISO()
          .substring(11, 16);
      }

      if (!hours) return "23:59";
      return new Date("1970-01-01T" + hours + ":00Z")
        .toTimeString()
        .substring(0, 5);
    };

  private checkPermissions(
    course: Course,
    user: User,
  ): AccessDenied | null {
    const isProgramAdmin = this.storageService.get("prop", "program_administrator");
    const mongoId = this.storageService.get("prop", "mongoId");

    if (
      course.assistants.includes(mongoId) &&
      course.assistants_no_mgr_rights &&
      !isProgramAdmin
    ) {
      return { denied: "assistant_no_mgr_rights" };
    }

    const courseMode = this.contentsService.getCourseMode(course._id);
    const instructor = this.junctionUserService.hasProfessorRights(course, user);

    if (!instructor || (instructor && courseMode === "student")) {
      return { denied: "not_instructor" };
    }

    return null;
  }
}

export default ActivitySettingsV2Service.getSingleton();

export { ActivitySettingsV2Service as ActivitySettingsV2ServiceInstance };


const formatDate = (
  dateString: string | null,
  globalTime: string,
  timeZone: string,
): [string, string] => {
  if (!dateString) return ["", globalTime || ""];
  const dateTime = window.luxon.DateTime.fromISO(dateString, { zone: timeZone });
  if (dateTime.year <= 1970 || dateTime.year >= 2049) return ["", globalTime || ""];
  return [dateTime.toFormat("MM/dd/yyyy"), dateTime.toFormat("HH:mm")];
};

const processResumeData = (resume: ResumeData): {
  notBlocked: boolean;
  defaultResumeAttempts: DefaultResumeAttempts;
} => {
  const notBlocked = resume.blocked === true;
  const defaultResumeAttempts = {
    ...resume.default,
    reverse_policy: !resume.default.submission_attempt_override,
  };
  return { notBlocked, defaultResumeAttempts };
};

const getSectionTemplateWarning = (course: Course): Warning | undefined =>
  course.section_type === "section" && course.use_section_template
    ? SECTION_TEMPLATE_WARNING
    : undefined;
